import React from "react";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  formatDueFriendly,
  formatDuration,
  isOpenTodo,
  parseTodoDue,
} from "../../utils/todoSchedule";

function TaskCard({ todo, onDone, compact }) {
  const due = parseTodoDue(todo);
  const duration = formatDuration(todo.duration);
  return (
    <Box
      w="100%"
      bg="white"
      borderRadius="xl"
      px={4}
      py={3}
      shadow="sm"
      borderWidth="1px"
      borderColor={todo.blocked ? "orange.200" : "gray.100"}
    >
      <HStack align="start" spacing={3}>
        <Button
          size="sm"
          variant="outline"
          colorScheme="green"
          flexShrink={0}
          onClick={() => onDone(todo.id)}
          aria-label={`Mark ${todo.task} done`}
        >
          Done
        </Button>
        <VStack align="start" spacing={1} flex={1} minW={0}>
          <HStack spacing={2} flexWrap="wrap">
            <Text fontWeight="semibold" fontSize="md" noOfLines={2}>{todo.task}</Text>
            {todo.blocked ? <Badge colorScheme="orange">Waiting</Badge> : null}
          </HStack>
          {!compact && todo.description ? (
            <Text fontSize="sm" color="gray.600" noOfLines={2}>{todo.description}</Text>
          ) : null}
          <HStack spacing={2} flexWrap="wrap" fontSize="sm" color="gray.500">
            {due ? <Text>{formatDueFriendly(due)}</Text> : <Text>No due date</Text>}
            {duration ? <Text>· {duration}</Text> : null}
          </HStack>
          {todo.explain ? (
            <Text fontSize="xs" color="gray.500" noOfLines={compact ? 1 : 3}>{todo.explain}</Text>
          ) : null}
        </VStack>
      </HStack>
    </Box>
  );
}

export default function SimpleTaskList({ todos, nextPlan, onDone }) {
  const open = todos.filter(isOpenTodo);
  const nextIds = new Set((nextPlan?.todos || []).map((row) => row.id));
  const suggested = (nextPlan?.todos || []).filter((row) => isOpenTodo(row));
  const rest = open
    .filter((row) => !nextIds.has(row.id))
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <Stack spacing={5} w="100%">
      {suggested.length ? (
        <Box>
          <Heading size="sm" mb={3} color="gray.700">Suggested now</Heading>
          <Stack spacing={3}>
            {suggested.map((todo) => (
              <TaskCard key={`next-${todo.id}`} todo={todo} onDone={onDone} compact />
            ))}
          </Stack>
        </Box>
      ) : null}

      <Box>
        <Heading size="sm" mb={3} color="gray.700">
          {suggested.length ? "Everything else" : "Your tasks"}
        </Heading>
        {rest.length ? (
          <Stack spacing={3}>
            {rest.map((todo) => (
              <TaskCard key={todo.id} todo={todo} onDone={onDone} />
            ))}
          </Stack>
        ) : (
          <Text color="gray.500" fontSize="sm">
            {open.length ? "All open tasks are in the suggested list above." : "No open tasks yet. Your agent can add them, or tap New task."}
          </Text>
        )}
      </Box>

      {nextPlan?.blocked?.length ? (
        <Box>
          <Heading size="sm" mb={2} color="gray.700">Waiting on something else</Heading>
          <Stack spacing={2}>
            {nextPlan.blocked.map((todo) => (
              <Box key={`blocked-${todo.id}`} bg="orange.50" borderRadius="lg" px={3} py={2}>
                <Text fontWeight="medium" fontSize="sm">{todo.task}</Text>
                {todo.explain ? <Text fontSize="xs" color="gray.600">{todo.explain}</Text> : null}
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}
